import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import styles from './CourseCard.module.scss';
import Button from '../Button';
import classNames from 'classnames';
import { Checkbox, Icon } from 'Components';
import ToggleButton from '../ToggleButton';
import DropdownMenu from '../Dropdown';
import AssignmentBadge from 'LearningManager/components/AssignmentBadge';
import { DETAILS_LABEL } from 'LearningManager/reports/Table/TrainingTable.constants';

interface Props {
	course: {
		id: number;
		title: string;
		meta?: string;
		tag?: string;
		tags?: string[];
		image: string;
		courseCount?: string;
		isMandatory?: boolean;
		isSelected?: boolean;
	};
	index: number;
	htmlId?: string;
	onClick: () => void;
	onSelect?: (id: number) => void;
	showCheckbox?: boolean;
	checked?: boolean;
	onCheck?: (id: number, checked: boolean) => void;
	showMenu?: boolean;
	onMenuAction?: (id: number, action: string) => void;
	ctaLabel?: string;
	ctaVariant?: 'border' | 'borderless';
	ctaClassName?: string;
	checkedClassName?: string;
	hideBadge?: boolean;
	hideRequiredSection?: boolean;
	hideMandatoryCover?: boolean;
	isRequired?: boolean;
	onRequiredChange?: (id: number, isRequired: boolean) => void;
}

interface RequiredSectionProps {
	checked: boolean;
	onChange: (checked: boolean) => void;
	t: TFunction
}

const RequiredSection: React.FC<RequiredSectionProps> = ({
	checked,
	onChange,
	t
}) => {
	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	return (
		<div className={styles['required-section']} onClick={handleToggle}>
			<div className={styles.heading}>{t('learning.courseDetails.badges.required')}</div>
			<ToggleButton checked={checked} size="small" onChange={onChange} />
		</div>
	);
};

const CourseCard: React.FC<Props> = ({
	course,
	index,
	htmlId,
	onClick,
	onSelect,
	showCheckbox,
	checked,
	onCheck,
	showMenu,
	onMenuAction,
	ctaLabel = DETAILS_LABEL,
	ctaVariant,
	ctaClassName,
	checkedClassName,
	hideBadge = false,
	hideRequiredSection = false,
	hideMandatoryCover = false,
	isRequired,
	onRequiredChange
}) => {
	const [localRequired, setLocalRequired] = useState(true);
	const { t } = useTranslation(); 

	const handleRequiredChange = (value: boolean) =>
		onRequiredChange
			? onRequiredChange(course.id, value)
			: setLocalRequired(value);

	const handleDetailsClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onClick();
	};

	return (
		<div
			id={htmlId}
			className={classNames(styles['course-card'], {
				[styles['course-card--selected']]: course.isSelected
			})}
			onClick={
				onSelect && !course.isMandatory
					? () => onSelect(course.id)
					: () => null
			}
		>
			{course.isSelected && (
				<div className={classNames(styles.checked, checkedClassName)}>
					<Icon type="check" />
				</div>
			)}

			{course.isMandatory && !hideMandatoryCover && (
				<div className={styles['card-cover']}>
					<span className={styles.message}>
						{t('learning.courseCard.mandatoryMessage')}
					</span>
				</div>
			)}

			<div className={styles['image-wrapper']}>
				<img src={course.image} alt={course.title} />

				{showCheckbox && (
					<div
						className={styles['card-checkbox']}
						onClick={(e) => e.stopPropagation()}
					>
						<Checkbox
							checked={checked}
							onChange={(e) =>
								onCheck?.(course.id, e.target.checked)
							}
						/>
					</div>
				)}

				{showMenu && (
					<div
						className={styles['card-menu']}
						onClick={(e) => e.stopPropagation()}
					>
						<DropdownMenu
							arrow={false}
							onChange={(key: string) =>
								onMenuAction?.(course.id, key)
							}
							options={[
								{
									key: 'view-details',
									label: t('learning.courseCard.viewDetails')
								},
								{
									key: 'reset-completion',
									label: t('learning.courseCard.resetCompletion')
								},
								{
									key: 'unassign',
									label: t('learning.courseCard.unassign'),
									danger: true
								}
							]}
						>
							<div className={styles['more-btn']}>
								<Icon type="optionIcon" role="img" />
							</div>
						</DropdownMenu>
					</div>
				)}

				{course.courseCount ? (
					<div className={styles.courseCount}>{course.courseCount}</div>
				) : (
					!hideBadge &&
					!showCheckbox &&
					!showMenu && <div className={styles.badge}>{index}</div>
				)}
			</div>

			<div className={styles.content}>
				<div className={styles.title}>{course.title}</div>

				{course.meta && <div className={styles.meta}>{course.meta}</div>}

				{course.tags && course.tags.length > 0 ? (
					<div className={styles.tags}>
						{course.tags.map((tag) => (
							<AssignmentBadge key={tag} type={tag} />
						))}
					</div>
				) : (
					course.tag && <span className={styles.tag}>{course.tag}</span>
				)}
			</div>

			<Button
				fullWidth
				size="small"
				variant={ctaVariant}
				className={ctaClassName}
				onClick={handleDetailsClick}
			>
				{t(ctaLabel)}
			</Button>

			{course.isSelected && !hideRequiredSection && (
				<RequiredSection
					t={t}
					checked={isRequired ?? localRequired}
					onChange={handleRequiredChange}
				/>
			)}
		</div>
	);
};

export default CourseCard;
